<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'vuex';
import { useAvatarColor } from 'dashboard/composables/useAvatarColor';
import { statusColorClasses } from 'dashboard/modules/kanban/constants/statusByCategory';

const props = defineProps({
  card: { type: Object, required: true },
  selectionMode: { type: Boolean, default: false },
  selected: { type: Boolean, default: false },
  keepActionsVisible: { type: Boolean, default: false },
  keepWonVisible: { type: Boolean, default: false },
});

const emit = defineEmits([
  'openConversation',
  'changeStatus',
  'cardClick',
  'openTasks',
  'select',
]);

const { avatarColor, initials: agentInitials } = useAvatarColor();

const isHovered = ref(false);
const cardRef = ref(null);

const { t } = useI18n();
const store = useStore();

const cardStatus = computed(() => props.card.status ?? 'open');

const pipelineCategory = computed(
  () => store.getters['kanban/activePipeline']?.category ?? 'sales'
);
const isPostSales = computed(() => pipelineCategory.value === 'post_sales');

const isOpen = computed(() => cardStatus.value === 'open');
const isWaiting = computed(() => cardStatus.value === 'waiting');
const isWon = computed(() => cardStatus.value === 'won');
const isLost = computed(() => cardStatus.value === 'lost');

const borderClass = computed(() => {
  const { border } = statusColorClasses(
    pipelineCategory.value,
    cardStatus.value
  );
  return `border-t-[3px] ${border}`;
});

const saleValue = computed(() => {
  const value = props.card.value;
  if (value === undefined || value === null || value === '') return null;
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value);
});

const schedulingDate = computed(() => props.card.closing_date ?? null);

function parseDateLocal(dateStr) {
  const [y, m, d] = dateStr.split('-').map(Number);
  return new Date(y, m - 1, d);
}

const formattedSchedulingDate = computed(() => {
  if (!schedulingDate.value) return null;
  return parseDateLocal(schedulingDate.value).toLocaleDateString('en', {
    month: 'short',
    day: 'numeric',
  });
});

function formatMeetingDateTime(timestamp) {
  if (!timestamp) return '';
  return new Date(timestamp * 1000)
    .toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    })
    .replace(', ', ' - ');
}

const meetingColorClass = computed(() => {
  if (!schedulingDate.value) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const tomorrow = new Date(today);
  tomorrow.setDate(tomorrow.getDate() + 1);
  const meeting = parseDateLocal(schedulingDate.value);
  if (meeting < today) return 'text-n-ruby-9';
  if (meeting.getTime() === today.getTime()) return 'text-n-slate-10';
  if (meeting.getTime() === tomorrow.getTime()) return 'text-n-amber-10';
  return 'text-n-slate-10';
});

const nextMeetingColorClass = computed(() => {
  if (!props.card.next_meeting) return '';
  return 'bg-n-iris-3 text-n-iris-11';
});

const lostReasonText = computed(() =>
  isPostSales.value ? props.card.cancel_reason : props.card.loss_reason
);

function countBusinessDays(fromMs, toMs) {
  let count = 0;
  const cursor = new Date(fromMs);
  cursor.setHours(0, 0, 0, 0);
  const end = new Date(toMs);
  end.setHours(0, 0, 0, 0);
  while (cursor < end) {
    const dow = cursor.getDay();
    if (dow !== 0 && dow !== 6) count += 1;
    cursor.setDate(cursor.getDate() + 1);
  }
  return count;
}

const stalledElapsed = computed(() => {
  if (!isOpen.value && !isWaiting.value) return null;
  const lastRef =
    props.card.stage_changed_at ?? props.card.last_activity_at ?? null;
  if (!lastRef) return null;
  return Math.max(0, Date.now() - (lastRef > 1e10 ? lastRef : lastRef * 1000));
});

const stalledDays = computed(() => {
  if (stalledElapsed.value === null) return null;
  const totalDays = Math.floor(stalledElapsed.value / 86400000);
  if (totalDays < 1) return 0;
  const fromMs = Date.now() - stalledElapsed.value;
  return countBusinessDays(fromMs, Date.now());
});

const stalledLabel = computed(() => {
  const ms = stalledElapsed.value;
  if (ms === null) return null;
  const mins = Math.floor(ms / 60000);
  const hours = Math.floor(ms / 3600000);
  if (stalledDays.value !== null && stalledDays.value >= 1)
    return `${stalledDays.value}d`;
  if (hours >= 1) return `${hours}h`;
  return `${mins}min`;
});

const stalledColorClass = computed(() => {
  if (stalledDays.value === null) return '';
  if (stalledDays.value >= 8) return 'text-n-ruby-9';
  if (stalledDays.value >= 4) return 'text-n-amber-10';
  return 'text-n-slate-10';
});

const priority = computed(() => props.card.conversation?.priority ?? null);

const priorityMeta = computed(() => {
  switch (priority.value) {
    case 'urgent':
      return {
        label: t('KANBAN.CARD.PRIORITY_URGENT'),
        bars: [true, true, true],
        color: '#ef4444',
      };
    case 'high':
      return {
        label: t('KANBAN.CARD.PRIORITY_HIGH'),
        bars: [true, true, false],
        color: '#f97316',
      };
    case 'medium':
      return {
        label: t('KANBAN.CARD.PRIORITY_MEDIUM'),
        bars: [true, true, false],
        color: '#f59e0b',
      };
    case 'low':
      return {
        label: t('KANBAN.CARD.PRIORITY_LOW'),
        bars: [true, false, false],
        color: '#94a3b8',
      };
    default:
      return null;
  }
});

const originTags = computed(() => {
  const oppSource = props.card.custom_attributes?.opp_source;
  if (oppSource) return [oppSource];
  const labels = props.card.contact?.labels ?? [];
  const ORIGIN_TAGS = [
    'Meta Ads',
    'Google Ads',
    'Prospecção',
    'Indicação',
    'Instagram',
    'Linkedin',
    'Site',
    'Google',
    'Loja',
  ];
  return labels.filter(l => ORIGIN_TAGS.includes(l.toLowerCase()));
});

const avatarInitial = computed(
  () => props.card.contact?.name?.[0]?.toUpperCase() ?? '?'
);

const ownerAgent = computed(
  () => props.card.assignee ?? props.card.conversation?.meta?.assignee ?? null
);

const ownerInitial = computed(() => {
  const name = ownerAgent.value?.name;
  return name ? agentInitials(name) : null;
});

const ownerName = computed(() => ownerAgent.value?.name ?? null);

const ownerThumbnail = computed(() => ownerAgent.value?.thumbnail ?? null);

const ownerAvatarColor = computed(() => avatarColor(ownerAgent.value ?? ''));

const hasMetaRow = computed(
  () =>
    ((isOpen.value || isWaiting.value) && stalledDays.value !== null) ||
    formattedSchedulingDate.value ||
    !!props.card.next_meeting
);

const saleCycleLabel = computed(() => {
  if (!isWon.value || !props.card.status_changed_at || !props.card.created_at)
    return null;
  const createdMs =
    props.card.created_at > 1e10
      ? props.card.created_at
      : props.card.created_at * 1000;
  const wonMs = props.card.status_changed_at * 1000;
  const diffMs = Math.max(0, wonMs - createdMs);

  const totalMinutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(totalMinutes / 60);
  const days = Math.floor(hours / 24);
  const remainingHours = hours % 24;
  const months = Math.floor(days / 30);
  const remainingDays = days % 30;

  const parts = [];
  if (months > 0) parts.push(`${months}m`);
  if (remainingDays > 0 || months > 0) {
    if (remainingDays > 0) parts.push(`${remainingDays}d`);
  }
  if (remainingHours > 0) parts.push(`${remainingHours}h`);

  if (parts.length === 0) {
    const mins = totalMinutes % 60;
    return `${mins}min`;
  }
  return parts.join(' ');
});

function onCardClick() {
  if (props.selectionMode) {
    emit('select', props.card.id);
  } else {
    emit('cardClick', props.card);
  }
}
</script>

<template>
  <div
    ref="cardRef"
    class="relative flex-shrink-0 rounded-lg bg-n-background border border-n-weak overflow-hidden cursor-grab active:cursor-grabbing transition-all hover:shadow-md hover:-translate-y-px"
    :class="[borderClass, selected ? 'bg-n-alpha-2' : '']"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
    @click="onCardClick"
  >
    <div
      v-if="selectionMode"
      class="absolute top-2 left-2 z-10"
      @click.stop="emit('select', card.id)"
    >
      <input
        type="checkbox"
        :checked="selected"
        class="size-3.5 accent-n-brand cursor-pointer"
      />
    </div>
    <div class="px-3 pt-2.5 pb-2">
      <!-- Bloco 1: avatar + nome + empresa + dono -->
      <div class="flex items-center justify-between gap-2 mb-1.5">
        <div class="flex items-center gap-2 min-w-0 flex-1">
          <div
            class="w-[30px] h-[30px] rounded-full flex-shrink-0 flex items-center justify-center text-white text-xs font-bold bg-n-brand overflow-hidden"
            :title="card.contact?.name"
          >
            <img
              v-if="card.contact?.avatar_url"
              :src="card.contact.avatar_url"
              class="w-[30px] h-[30px] rounded-full object-cover"
              :alt="card.contact?.name"
            />
            <span v-else>{{ avatarInitial }}</span>
          </div>
          <div class="min-w-0 flex-1">
            <div class="text-sm font-semibold text-n-slate-12 truncate">
              {{ card.title || card.contact?.name }}
            </div>
            <div
              v-if="card.title && card.contact?.name"
              class="text-xs text-n-slate-10 truncate"
            >
              {{ card.contact?.name }}
            </div>
            <div
              v-else-if="
                card.contact?.company_name || card.contact?.company?.name
              "
              class="text-xs text-n-slate-10 truncate"
            >
              {{ card.contact?.company_name || card.contact?.company?.name }}
            </div>
          </div>
        </div>
        <div class="flex items-end gap-1 flex-shrink-0">
          <span v-if="priorityMeta" :title="priorityMeta.label">
            <svg width="11" height="10" viewBox="0 0 16 14" fill="none">
              <rect
                x="0"
                y="6"
                width="3"
                height="8"
                rx="1"
                :fill="
                  priorityMeta.bars[0] ? priorityMeta.color : 'currentColor'
                "
                :class="!priorityMeta.bars[0] ? 'text-n-slate-5' : ''"
              />
              <rect
                x="5"
                y="3"
                width="3"
                height="11"
                rx="1"
                :fill="
                  priorityMeta.bars[1] ? priorityMeta.color : 'currentColor'
                "
                :class="!priorityMeta.bars[1] ? 'text-n-slate-5' : ''"
              />
              <rect
                x="10"
                y="0"
                width="3"
                height="14"
                rx="1"
                :fill="
                  priorityMeta.bars[2] ? priorityMeta.color : 'currentColor'
                "
                :class="!priorityMeta.bars[2] ? 'text-n-slate-5' : ''"
              />
            </svg>
          </span>
          <div
            v-if="ownerInitial"
            class="w-[22px] h-[22px] rounded-full flex-shrink-0 flex items-center justify-center text-white text-[7.5px] font-bold ring-2 ring-n-weak cursor-default overflow-hidden"
            :style="{
              backgroundColor: ownerThumbnail
                ? 'transparent'
                : ownerAvatarColor,
            }"
            :title="ownerName"
          >
            <img
              v-if="ownerThumbnail"
              :src="ownerThumbnail"
              class="w-full h-full object-cover"
              :alt="ownerName"
            />
            <span v-else>{{ ownerInitial }}</span>
          </div>
        </div>
      </div>

      <!-- Bloco 2 (meta): perdido → data de perda + motivo | aberto → relógio, calendário, reunião, prioridade -->
      <template v-if="isLost">
        <div class="flex items-center gap-2 flex-wrap mb-1.5">
          <span
            v-if="card.status_changed_at || card.stage_changed_at"
            class="flex items-center gap-1 text-[11px] text-n-ruby-9"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <rect x="3" y="4" width="15" height="15" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{
              new Date(
                (card.status_changed_at ?? card.stage_changed_at) * 1000
              ).toLocaleDateString('en', { month: 'short', day: 'numeric' })
            }}
          </span>
          <span
            v-if="
              (card.status_changed_at || card.stage_changed_at) &&
              lostReasonText
            "
            class="w-px h-2.5 bg-n-weak flex-shrink-0"
          />
          <span
            v-if="lostReasonText"
            class="flex items-center gap-1 text-[11px] text-n-ruby-9 truncate"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.3"
              class="flex-shrink-0"
            >
              <polyline points="23 18 13.5 8.5 8.5 13.5 1 6" />
              <polyline points="17 18 23 18 23 12" />
            </svg>
            <span class="truncate">{{ lostReasonText }}</span>
          </span>
        </div>
      </template>

      <template v-else-if="isWon">
        <div
          v-if="card.status_changed_at"
          class="flex items-center gap-2 flex-wrap mb-1.5"
        >
          <span class="flex items-center gap-1 text-[11px] text-n-teal-10">
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <rect x="3" y="4" width="15" height="15" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{
              new Date(card.status_changed_at * 1000).toLocaleDateString('en', {
                month: 'short',
                day: 'numeric',
              })
            }}
          </span>
          <span
            v-if="saleCycleLabel !== null"
            class="w-px h-2.5 bg-n-weak flex-shrink-0"
          />
          <span
            v-if="saleCycleLabel !== null"
            class="flex items-center gap-1 text-[11px] text-n-teal-10"
            :title="`Ciclo de venda: ${saleCycleLabel}`"
          >
            <!-- ícone de raio: velocidade do ciclo de venda -->
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <polyline points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
            </svg>
            {{ saleCycleLabel }}
          </span>
        </div>
      </template>

      <div
        v-else-if="hasMetaRow"
        class="flex items-center gap-2 flex-wrap mb-1.5"
      >
        <template v-if="isWaiting">
          <span
            class="px-1.5 py-0.5 rounded text-[11px] font-medium bg-n-amber-3 text-n-amber-11 flex-shrink-0"
          >
            {{ t('KANBAN.CARD.ACTION_WAITING') }}
          </span>
          <span
            v-if="
              stalledLabel !== null || formattedSchedulingDate || priorityMeta
            "
            class="w-px h-2.5 bg-n-weak flex-shrink-0"
          />
        </template>
        <template v-if="(isOpen || isWaiting) && stalledLabel !== null">
          <span
            class="flex items-center gap-1 text-[11px]"
            :class="stalledColorClass"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            {{ stalledLabel }}
          </span>
          <span
            v-if="formattedSchedulingDate || priorityMeta"
            class="w-px h-2.5 bg-n-weak flex-shrink-0"
          />
        </template>

        <template v-if="formattedSchedulingDate">
          <span
            class="flex items-center gap-1 text-[11px]"
            :class="meetingColorClass"
          >
            <svg
              width="11"
              height="11"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2.2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            {{ formattedSchedulingDate }}
          </span>
        </template>

        <template v-if="card.next_meeting">
          <span
            v-if="
              formattedSchedulingDate ||
              ((isOpen || isWaiting) && stalledLabel !== null)
            "
            class="w-px h-2.5 bg-n-weak flex-shrink-0"
          />
          <span
            class="px-1.5 py-0.5 rounded text-[11px] font-medium flex-shrink-0"
            :class="nextMeetingColorClass"
            :title="formatMeetingDateTime(card.next_meeting.due_date)"
          >
            {{ formatMeetingDateTime(card.next_meeting.due_date) }}
          </span>
        </template>
      </div>

      <!-- Bloco 4: tags + valor + ícone conversa -->
      <div class="flex items-center gap-1">
        <div class="flex items-center gap-1 flex-wrap flex-1">
          <span
            v-for="tag in originTags"
            :key="tag"
            class="px-1.5 py-0.5 rounded text-[11px] font-medium bg-n-blue-3 text-n-blue-11"
          >
            {{ tag }}
          </span>
          <span
            v-if="saleValue"
            class="px-1.5 py-0.5 rounded text-[11px] font-semibold bg-n-teal-3 text-n-teal-11"
          >
            {{ saleValue }}
          </span>
        </div>

        <div class="flex items-center gap-px flex-shrink-0">
          <!-- Ícone de tarefas: sempre visível se tiver alerta, senão só no hover -->
          <button
            :title="$t('KANBAN.OPPORTUNITY_MODAL.NAV_TASKS')"
            class="relative flex items-center justify-center text-n-slate-8 hover:text-n-brand transition-colors p-1"
            :class="{
              'opacity-0 pointer-events-none': !isHovered && !card.task_alert,
            }"
            @click.stop="emit('openTasks', card)"
          >
            <svg
              width="13"
              height="13"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.9"
            >
              <path d="M9 11l3 3L22 4" />
              <path
                d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"
              />
            </svg>
            <span
              v-if="card.task_alert"
              class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full border border-white dark:border-[#16171A]"
              :class="
                card.task_alert === 'overdue'
                  ? 'bg-n-ruby-9'
                  : card.task_alert === 'due_today'
                    ? 'bg-n-teal-9'
                    : 'bg-n-amber-9'
              "
            />
          </button>
          <!-- Ícone de conversa: sempre visível se tiver alerta, senão só no hover -->
          <button
            :tabindex="isHovered || card.conversation_alert ? 0 : -1"
            :title="$t('KANBAN.CARD.OPEN_CONVERSATION')"
            class="relative flex items-center justify-center text-n-slate-8 hover:text-n-blue-11 transition-colors p-1"
            :class="{
              'opacity-0 pointer-events-none':
                !isHovered && !card.conversation_alert,
            }"
            @click.stop="emit('openConversation', card)"
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
            >
              <path
                d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"
              />
            </svg>
            <span
              v-if="card.conversation_alert"
              class="absolute top-0.5 right-0.5 w-2 h-2 rounded-full border border-white dark:border-[#16171A]"
              :class="
                card.conversation_alert === 'today'
                  ? 'bg-n-teal-9'
                  : 'bg-n-ruby-9'
              "
            />
          </button>
        </div>
      </div>
    </div>

    <!-- Barra de ações de status (hover) -->
    <div
      v-show="isHovered || keepActionsVisible || keepWonVisible"
      class="flex border-t border-n-weak"
    >
      <template v-if="isOpen || isWaiting">
        <button
          v-if="!keepWonVisible"
          class="flex-1 py-1.5 text-[11px] font-semibold text-n-ruby-9 hover:bg-n-ruby-2 transition-colors focus:outline-none"
          :class="{ 'bg-n-ruby-2': keepActionsVisible }"
          @click.stop="
            e =>
              emit('changeStatus', {
                card: props.card,
                targetStatus: 'lost',
                anchor: e.currentTarget.getBoundingClientRect(),
              })
          "
        >
          {{
            isPostSales
              ? t('KANBAN.CARD.ACTION_LOST_POST_SALES')
              : t('KANBAN.CARD.ACTION_LOST')
          }}
        </button>
        <span
          v-if="!keepActionsVisible && !keepWonVisible"
          class="w-px bg-n-weak"
        />
        <button
          v-if="isPostSales && !keepActionsVisible && !keepWonVisible"
          class="flex-1 py-1.5 text-[11px] font-semibold text-n-amber-10 hover:bg-n-amber-2 transition-colors"
          @click.stop="
            e =>
              emit('changeStatus', {
                card: props.card,
                targetStatus: isWaiting ? 'open' : 'waiting',
                anchor: isWaiting
                  ? null
                  : e.currentTarget.getBoundingClientRect(),
              })
          "
        >
          {{
            isWaiting
              ? t('KANBAN.CARD.ACTION_REOPEN')
              : t('KANBAN.CARD.ACTION_WAITING')
          }}
        </button>
        <span
          v-if="isPostSales && !keepActionsVisible && !keepWonVisible"
          class="w-px bg-n-weak"
        />
        <button
          v-if="!keepActionsVisible"
          class="flex-1 py-1.5 text-[11px] font-semibold text-n-teal-10 hover:bg-n-teal-2 transition-colors"
          :class="{ 'bg-n-teal-2': keepWonVisible }"
          @click.stop="
            emit('changeStatus', {
              card: props.card,
              targetStatus: 'won',
              anchor: cardRef?.getBoundingClientRect(),
            })
          "
        >
          {{
            isPostSales
              ? t('KANBAN.CARD.ACTION_WON_POST_SALES')
              : t('KANBAN.CARD.ACTION_WON')
          }}
        </button>
      </template>
      <template v-else>
        <button
          class="flex-1 py-1.5 text-[11px] font-semibold text-n-blue-11 hover:bg-n-blue-2 transition-colors"
          @click.stop="
            emit('changeStatus', { card: props.card, targetStatus: 'open' })
          "
        >
          {{ t('KANBAN.CARD.ACTION_REOPEN') }}
        </button>
      </template>
    </div>
  </div>
</template>
